// WireframeWave.tsx
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, shaderMaterial } from "@react-three/drei";
import * as THREE from "three";
import { extend } from "@react-three/fiber";
import { useRef, useEffect, useMemo } from "react";
import { Mail, MapPin, ArrowUpRight } from "lucide-react";

// Reads the --home-* theme variables from App.css so this scene stays in sync
// with the rest of the homepage without baking hex values in here.
const getHomeColor = (cssVarName) =>
  getComputedStyle(document.documentElement).getPropertyValue(cssVarName).trim();

// Plane parameters shared between the geometry, the wave displacement, and the
// hover math, so the highlighted triangle always lines up with the rendered one.
const PLANE_W = 15;
const PLANE_H = 40;
const SEGS_X = 50;
const SEGS_Y = 50;

// 1. Create a custom shader material (unaffected by hover — pure ocean gradient).
const OceanMaterial = shaderMaterial(
  { uTime: 0, uColor: new THREE.Color(getHomeColor("--home-cta")) },
  // Vertex Shader
  `
    uniform float uTime;
    varying vec2 vUv;

    void main() {
      vUv = uv;
      vec3 pos = position;

      // Large slow wave (rolling forward)
      pos.z += sin((pos.x + uTime * 0.5) * 2.0) * 0.2;

      // Smaller fast ripples
      pos.z += cos((pos.x * 4.0 + pos.y * 4.0 + uTime * 2.0)) * 0.05;

      gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
    }
  `,
  // Fragment Shader
  `
    uniform vec3 uColor;
    varying vec2 vUv;

    void main() {
      gl_FragColor = vec4(uColor, 1.0);
}`,
);

extend({ OceanMaterial });

// 2. A second material that fills in exactly one triangle — the one under the
// cursor — using a per-triangle id attribute so neighbours never light up.
const FillMaterial = shaderMaterial(
  { uTime: 0, uHoverFaceId: -1, uHoverStrength: 0, uFillColor: new THREE.Color(getHomeColor("--home-cta")) },
  // Vertex Shader — identical displacement to the ocean wave, plus the face id.
  `
    uniform float uTime;
    attribute float aFaceId;
    varying float vFaceId;

    void main() {
      vFaceId = aFaceId;
      vec3 pos = position;
      pos.z += sin((pos.x + uTime * 0.5) * 2.0) * 0.2;
      pos.z += cos((pos.x * 4.0 + pos.y * 4.0 + uTime * 2.0)) * 0.05;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
    }
  `,
  // Fragment Shader — only the hovered triangle's id survives; everything else discards.
  `
    uniform float uHoverFaceId;
    uniform float uHoverStrength;
    uniform vec3 uFillColor;
    varying float vFaceId;

    void main() {
      if (uHoverFaceId < 0.0 || abs(vFaceId - uHoverFaceId) > 0.5) discard;
      gl_FragColor = vec4(uFillColor, uHoverStrength);
    }
  `,
);

extend({ FillMaterial });

// Build a non-indexed clone of the plane so each triangle owns its own 3
// vertices, then stamp a constant face id across each triangle's vertices.
// The triangle order here matches PlaneGeometry's own index construction
// (row-major cells, 2 triangles per cell), so id `t` computed from a hit
// point's grid cell always points at the triangle actually drawn there.
function buildFillGeometry() {
  const geo = new THREE.PlaneGeometry(PLANE_W, PLANE_H, SEGS_X, SEGS_Y).toNonIndexed();
  const triCount = SEGS_X * SEGS_Y * 2;
  const faceIds = new Float32Array(triCount * 3);
  for (let t = 0; t < triCount; t++) {
    faceIds[t * 3] = t;
    faceIds[t * 3 + 1] = t;
    faceIds[t * 3 + 2] = t;
  }
  geo.setAttribute("aFaceId", new THREE.BufferAttribute(faceIds, 1));
  return geo;
}

// Given a local-space hit point on the (undisplaced) plane, find which single
// triangle id it falls inside — mirrors PlaneGeometry's a/b/d + b/c/d split.
function pickFaceId(localX, localY) {
  const halfW = PLANE_W / 2, halfH = PLANE_H / 2;
  const segW = PLANE_W / SEGS_X, segH = PLANE_H / SEGS_Y;
  const ixF = (localX + halfW) / segW;
  const iyF = (halfH - localY) / segH;
  const cellX = Math.min(SEGS_X - 1, Math.max(0, Math.floor(ixF)));
  const cellY = Math.min(SEGS_Y - 1, Math.max(0, Math.floor(iyF)));
  const u = ixF - cellX;
  const v = iyF - cellY;
  const half = u + v <= 1 ? 0 : 1;
  return (cellY * SEGS_X + cellX) * 2 + half;
}

// The exact same wave formula the shaders use, evaluated on the CPU.
function waveHeight(x, y, t) {
  return Math.sin((x + t * 0.5) * 2.0) * 0.2 + Math.cos((x * 4.0 + y * 4.0 + t * 2.0)) * 0.05;
}

function OceanWave() {
  const materialRef = useRef();
  const fillMaterialRef = useRef();
  const groupRef = useRef();
  const hover = useRef({ faceId: -1, active: 0 });
  const timeRef = useRef(0);
  const { camera, gl } = useThree();
  const fillGeometry = useMemo(() => buildFillGeometry(), []);

  // Plane-intersection raycast (robust, no geometry/bounding-sphere/faceIndex
  // dependence), refined iteratively against the wave's actual height at the
  // guessed point — since the camera grazes the surface at a shallow angle,
  // even the small wave displacement shifts the visual hit point noticeably,
  // so a flat-plane guess alone isn't enough.
  useEffect(() => {
    const canvas = gl.domElement;
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const worldNormal = new THREE.Vector3();
    const plane = new THREE.Plane();
    const offsetPoint = new THREE.Vector3();
    const worldHit = new THREE.Vector3();

    const updateFromClient = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      const inside = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
      if (!inside || !groupRef.current) {
        hover.current.active = 0;
        return;
      }
      ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);

      worldNormal.set(0, 0, 1).applyQuaternion(groupRef.current.quaternion);

      // Pass 0: flat-plane guess. Passes 1-2: re-intersect a plane offset by
      // the wave's height at the previous guess, converging on the real point.
      let local = null;
      let z = 0;
      for (let pass = 0; pass < 3; pass++) {
        offsetPoint.set(0, 0, z).applyQuaternion(groupRef.current.quaternion).add(groupRef.current.position);
        plane.setFromNormalAndCoplanarPoint(worldNormal, offsetPoint);
        if (!raycaster.ray.intersectPlane(plane, worldHit)) {
          local = null;
          break;
        }
        local = groupRef.current.worldToLocal(worldHit.clone());
        z = waveHeight(local.x, local.y, timeRef.current);
      }

      if (!local || Math.abs(local.x) > PLANE_W / 2 || Math.abs(local.y) > PLANE_H / 2) {
        hover.current.active = 0;
        return;
      }
      hover.current.faceId = pickFaceId(local.x, local.y);
      hover.current.active = 1;
    };

    const onPointerMove = (e) => updateFromClient(e.clientX, e.clientY);
    const onPointerLeave = () => { hover.current.active = 0; };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerleave", onPointerLeave);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [camera, gl]);

  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    timeRef.current = t;
    if (materialRef.current) materialRef.current.uTime = t;
    if (fillMaterialRef.current) {
      fillMaterialRef.current.uTime = t;
      fillMaterialRef.current.uHoverFaceId = hover.current.faceId;
      fillMaterialRef.current.uHoverStrength = THREE.MathUtils.lerp(
        fillMaterialRef.current.uHoverStrength,
        hover.current.active,
        0.2,
      );
    }

    // Orbit around the center (0, 0, 0)
    const radius = 1;
    const speed = 0.1; // smaller = slower

    camera.position.x = Math.sin(t * speed) * radius;
    camera.position.z = Math.cos(t * speed) * radius;
    camera.position.y = 0.5; // height above the plane

    camera.lookAt(0, 0.1, 0); // always look at center
  });

  return (
    <group ref={groupRef} rotation={[Math.PI / 2, 0, Math.PI / 2]}>
      {/* Solid fill for the single hovered triangle, drawn just behind the wireframe. */}
      <mesh geometry={fillGeometry}>
        <fillMaterial ref={fillMaterialRef} transparent depthWrite={false} side={THREE.DoubleSide} polygonOffset polygonOffsetFactor={1} polygonOffsetUnits={1} />
      </mesh>
      {/* Wider plane: looks like ocean */}
      <mesh>
        <planeGeometry args={[PLANE_W, PLANE_H, SEGS_X, SEGS_Y]} />
        <oceanMaterial ref={materialRef} wireframe side={THREE.FrontSide} />
      </mesh>
    </group>
  );
}

export default function Footer() {
  return (
    <div className="relative w-full h-screen bg-[var(--home-bg)]">
      <div className="absolute flex flex-col items-center justify-start left-0 top-0 w-full h-full font-milker pt-[90px] px-[25px] lg:px-[65px] z-10 pointer-events-none">
        <div className="flex flex-col lg:flex-row items-start justify-between w-full h-full text-[var(--home-primary)]">
          <div className="flex flex-col gap-2 text-xl lg:text-[18px] pointer-events-auto bg-[var(--home-bg)]/55 backdrop-blur-md rounded-xl p-4 lg:bg-transparent lg:backdrop-blur-none lg:rounded-none lg:p-0">
            <a href="mailto:shreeramganeshan96@gmail.com" className="flex items-center gap-2 hover:opacity-60 transition w-fit">
              <Mail size={16} strokeWidth={1.75} /> shreeramganeshan96@gmail.com
            </a>
            <span className="flex items-center gap-2 opacity-80">
              <MapPin size={16} strokeWidth={1.75} /> Navi Mumbai
            </span>
          </div>
          <div className="flex gap-8 items-center pointer-events-auto bg-[var(--home-bg)]/55 backdrop-blur-md rounded-xl p-4 mt-4 lg:mt-0 lg:bg-transparent lg:backdrop-blur-none lg:rounded-none lg:p-0">
            <a className="hover:scale-110 transition" href="/skills">
              Skills
            </a>
            <a className="hover:scale-110 transition" href="/work">
              Work
            </a>
            <a className="hover:scale-110 transition" href="/experiments">
              Experiments
            </a>
            <a className="hover:scale-110 transition" href="https://x.com/shreeram_dev">
              Twitter / X
            </a>
          </div>
        </div>
        <a href="mailto:shreeramganeshan96@gmail.com" className="pointer-events-auto group">
          <div className="w-full mb-4 flex items-center justify-center gap-4 text-center text-4xl lg:text-[144px] text-[var(--home-primary)] p-12 lg:p-36 bg-[var(--home-bg)] border border-white/10 rounded-xl lg:rounded-[60px] cursor-pointer hover:text-[var(--home-bg)] hover:scale-[1.03] hover:bg-[var(--home-cta)] hover:border hover:border-[var(--home-cta-dark)] transition-all h-fit">
            <span>Let's Talk</span>
            <ArrowUpRight className="w-8 h-8 lg:w-20 lg:h-20 shrink-0 transition-transform group-hover:translate-x-2 group-hover:-translate-y-2" strokeWidth={1.25} />
          </div>
        </a>
      </div>
      <Canvas camera={{ position: [0, 1.5, 5], fov: 75, far: 10 }}>
        <OceanWave />
        {/* <OrbitControls /> */}
      </Canvas>
    </div>
  );
}
